import { z } from "zod";
/** Reusable Zod fragments shared across SOLVE workspace cards. */
/** ShortText's bound, exported so an editing field caps input at the same
 *  limit the schema enforces (BUG-6: the UI once allowed 500 and the save
 *  failed silently). */
export const SHORT_TEXT_MAX = 255;
export const ShortText = z.string().min(0).max(SHORT_TEXT_MAX);
export const MediumText = z.string().min(0).max(500);
export const LongText = z.string().min(0).max(2000);
/** A hosted URL (http/https). Sized for real-world links — signed dashboard /
 *  runbook URLs routinely exceed the 500-char `MediumText` cap, which would
 *  silently drop them on re-validation. Do NOT use `MediumText` for URL fields. */
export const UrlText = z.string().min(0).max(2048);
/** An image source: a hosted URL OR an inline `data:` URI from an in-canvas
 *  upload. Bound to hold the image widget's max upload (512 KB → ~699 K base64
 *  chars) with headroom, so a saved upload survives `paramsFromY`'s re-validation
 *  instead of coercing to a blank card + orphaning oversized bytes in the Y.Doc.
 *  Keep this ≥ the data-URI length implied by the UI's `MAX_UPLOAD_BYTES`
 *  (packages/ui image widget); the coupling is asserted in the ui test suite. */
export const ImageSrcText = z.string().min(0).max(1_400_000);
export const HexColor = z
    .string()
    .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "must be a hex colour like #fa0 or #ffaa00");
export const IsoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "must be YYYY-MM-DD")
    .refine((value) => {
    const [year, month, day] = value.split("-").map(Number);
    if (year === undefined || month === undefined || day === undefined) {
        return false;
    }
    const date = new Date(Date.UTC(year, month - 1, day));
    return (date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day);
}, "must be a valid calendar date");
/** Power-of-two-ish ordinal band for Impact / Effort / Cost on countermeasures. */
export const OrdinalBand = z.enum(["Low", "Med", "High"]);
/** Generic tone for status pills, quadrant cells, etc. */
export const Tone = z.enum(["good", "warn", "bad", "neutral"]);
/** Direction arrow for trend metrics. */
export const Direction = z.enum(["up", "down", "flat"]);
/**
 * "Problem Snapshot" metric used by the Tags quadrants in Specify.
 * One per quadrant: Trend / Actual / Gap / Standard. All fields optional
 * so sparse SOLVEs still render the 4-quadrant skeleton.
 */
export const ProblemSnapshotMetric = z
    .object({
    value: ShortText,
    caption: MediumText.optional(),
    direction: Direction.optional(),
    tone: Tone.optional(),
})
    .strict();
/** Containment action status (Oppress). */
export const ContainmentStatus = z.enum([
    "Done",
    "In Progress",
    "At Risk",
    "Blocked",
]);
/** Countermeasure status (Verify). */
export const CountermeasureStatus = z.enum([
    "Proposed",
    "Approved",
    "In Design",
    "Trial",
    "Done",
]);
/** Stakeholder map quadrant key (Verify). */
export const StakeholderQuadrant = z.enum([
    "satisfy",
    "manage",
    "monitor",
    "inform",
]);
/** Escalation matrix dimensions (Ensure). */
export const EscalationSeverity = z.enum(["High", "Medium", "Low"]);
export const EscalationLevel = z.enum(["Owner", "Lead", "Director"]);
export const EscalationTone = z.enum(["neutral", "watch", "warn", "critical"]);
/** Fishbone canonical 6M categories — branches may also use arbitrary strings. */
export const FISHBONE_KNOWN_CATEGORIES = [
    "Man",
    "Method",
    "Machine",
    "Material",
    "Measurement",
    "Environment",
];
export const FishboneCategory = z.union([
    z.enum(FISHBONE_KNOWN_CATEGORIES),
    ShortText,
]);
/** id string used in many list-of-entry schemas. */
export const idString = z.string().min(1).max(64);
/**
 * Causal-chain step (Look).
 *
 * PB-4923 added `id`. It is OPTIONAL only so rows written before the backfill
 * migration still validate — every writer mints one, and an item without one
 * cannot be addressed by a bounded operation (the collection falls back to
 * whole-value replacement, which is the pre-PB-4923 behaviour).
 *
 * The step's `label` is an ORDINAL ("Why 3") that renumbers when a step is
 * inserted, so it could never serve as identity: using it would be array
 * position under another name, which is exactly what the design forbids.
 */
export const CausalStep = z
    .object({
    id: idString.optional(),
    label: ShortText,
    body: MediumText,
    isRoot: z.boolean().optional(),
})
    .strict();
/**
 * The published shape of a causal step BEFORE `id`, retained byte-identically
 * because Player AI catalogues V3–V9 embed it and are resolved by exact
 * checksum. It validates nothing live.
 */
export const RetiredCausalStepV1 = z
    .object({
    label: ShortText,
    body: MediumText,
    isRoot: z.boolean().optional(),
})
    .strict();
//# sourceMappingURL=common.js.map